import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Radio, Volume2, VolumeX, MessageSquare, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveMonitor = ({ password }) => {
  const [soundOn, setSoundOn] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [log, setLog] = useState([]);

  const audioRef = useRef(null);
  const soundRef = useRef(false);
  const knownSessions = useRef(new Set());
  const knownChats = useRef(new Set());
  const seededRef = useRef(false);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioRef.current = new Ctx();
    }
    if (audioRef.current.state === "suspended") audioRef.current.resume();
    return audioRef.current;
  };

  const chime = (type) => {
    if (!soundRef.current) return;
    const ctx = ensureAudio();
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      const notes = type === "chat" ? [880, 1174.7] : [659.25];
      notes.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        o.connect(g);
        g.connect(ctx.destination);
        const t0 = now + i * 0.18;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
        o.start(t0);
        o.stop(t0 + 0.37);
      });
    } catch (e) {
      /* ignore */
    }
  };

  const enableSound = () => {
    ensureAudio();
    soundRef.current = true;
    setSoundOn(true);
    chime("visit");
  };
  const disableSound = () => {
    soundRef.current = false;
    setSoundOn(false);
  };

  const pushLog = (entry) =>
    setLog((l) => [{ ...entry, ts: new Date().toLocaleTimeString() }, ...l].slice(0, 30));

  const poll = async () => {
    try {
      const res = await axios.get(`${API}/admin/presence`, { params: { password } });
      const { active = [], chats = [], active_count = 0 } = res.data || {};
      setActiveCount(active_count);
      const firstRun = !seededRef.current;

      active.forEach((v) => {
        if (!knownSessions.current.has(v.session_id)) {
          knownSessions.current.add(v.session_id);
          if (!firstRun) {
            chime("visit");
            pushLog({ id: `v-${v.session_id}-${v.last_seen}`, kind: "visit", text: `New visitor on ${v.path || "the site"}` });
          }
        }
      });

      chats.forEach((c) => {
        if (!knownChats.current.has(c.id)) {
          knownChats.current.add(c.id);
          if (!firstRun) {
            chime("chat");
            pushLog({ id: `c-${c.id}`, kind: "chat", text: `Live chat started${c.name ? ` by ${c.name}` : ""}` });
          }
        }
      });

      seededRef.current = true;
    } catch (e) {
      /* ignore transient poll errors */
    }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  return (
    <section data-testid="live-monitor" className="bg-[#111111] border border-white/10 rounded-2xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="relative flex items-center justify-center w-11 h-11 rounded-full bg-[#E50914]/15 text-[#E50914]">
            <Radio size={20} />
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#1B8A5A] border-2 border-[#111111] animate-pulse" />
          </span>
          <div>
            <h2 className="font-cond font-bold uppercase tracking-wide text-white text-lg leading-none">
              Live <span className="text-[#E50914]">Monitor</span>
            </h2>
            <p className="text-white/40 text-xs mt-1">Visitors on the site right now</p>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <div className="text-right">
            <p data-testid="monitor-active-count" className="font-display text-[#F2C04C] text-3xl leading-none">{activeCount}</p>
            <p className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">Active now</p>
          </div>
          {soundOn ? (
            <button
              onClick={disableSound}
              data-testid="monitor-sound-toggle"
              className="inline-flex items-center gap-2 font-cond uppercase text-xs tracking-widest rounded-full px-4 py-2.5 bg-[#1B8A5A] text-white hover:bg-[#15734a]"
            >
              <Volume2 size={15} /> Alerts On
            </button>
          ) : (
            <button
              onClick={enableSound}
              data-testid="monitor-sound-toggle"
              className="inline-flex items-center gap-2 font-cond uppercase text-xs tracking-widest rounded-full px-4 py-2.5 bg-[#E50914] text-white hover:bg-[#c20710]"
            >
              <VolumeX size={15} /> Enable Alerts
            </button>
          )}
        </div>
      </div>

      {!soundOn && (
        <p className="mt-3 text-[#F2C04C]/80 text-xs">
          Click <span className="font-bold">Enable Alerts</span> to hear a chime when a visitor lands on the site or starts a chat.
        </p>
      )}

      <div className="mt-4 border-t border-white/10 pt-3 space-y-2 max-h-52 overflow-y-auto">
        {log.length === 0 ? (
          <p className="text-white/30 text-xs">Waiting for activity…</p>
        ) : (
          log.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm">
              {e.kind === "chat" ? (
                <MessageSquare size={14} className="text-[#E50914] shrink-0" />
              ) : (
                <Users size={14} className="text-[#6EE6E6] shrink-0" />
              )}
              <span className="text-white/80">{e.text}</span>
              <span className="text-white/30 text-xs ml-auto whitespace-nowrap">{e.ts}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default LiveMonitor;
